import { ReactNode, useRef } from 'react';

import {
  FieldInputProps,
  FieldProps,
  FieldRenderProps,
  useField,
} from 'react-final-form';

import { AddressInput as Input } from '@vk-hr-tek/ui/input';

import { composeValidators } from '../validation';

/**
 * Функция-обработчик изменения значения ввода.
 *
 * @param input - Объект с пропсами ввода из react-final-form.
 * @param onChange - Дополнительная функция-обработчик изменения значения.
 * @returns Функция, которая вызывает onChange из react-final-form и дополнительную onChange, если она передана.
 */
const handleChange =
  (
    input: FieldInputProps<
      string | string[] | number | number[] | undefined,
      HTMLElement
    >,
    onChange?: (value: string | undefined) => void,
  ) =>
  (value: string | undefined) => {
    input.onChange(value);

    if (onChange) {
      onChange(value);
    }
  };

const requestErr =
  'Сервис подсказки адресов временно недоступен. Повторите попытку позднее';
const completenessErr =
  'Нужно выбрать адрес из списка с содержанием: индекс, регион, населенный пункт, улица, дом/квартира';

const MAX_CACHE_SIZE = 50;

/**
 * Записывает значение в кэш с LRU-вытеснением: при превышении MAX_CACHE_SIZE
 * удаляется наиболее давно использованный ключ.
 */
const lruSet = <T,>(cache: Map<string, T>, key: string, value: T): void => {
  cache.set(key, value);

  if (cache.size > MAX_CACHE_SIZE) {
    const oldestKey = cache.keys().next().value;

    if (oldestKey !== undefined) {
      cache.delete(oldestKey);
    }
  }
};

/**
 * Фильтр для загрузки элементов.
 */
interface Filter {
  /**
   * Максимальное количество элементов для загрузки.
   */
  limit: number;

  /**
   * Смещение для пагинации.
   */
  offset: number;

  /**
   * Строка запроса для фильтрации.
   */
  query: string;
}

/**
 * Свойства компонента {@link AddressInput}
 */
interface AddressInputProps {
  /**
   * Имя поля формы.
   */
  name: string;

  /**
   * Функция для загрузки элементов.
   */
  loadItems: (
    filter: Filter,
  ) => Promise<{ value: string; addressCompleteness?: number }[]>;

  /**
   * Текст метки для поля формы.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Дополнительная функция-обработчик изменения значения.
   */
  onChange?: (value: string | undefined) => void;

  /**
   * Плейсхолдер для поля ввода.
   */
  placeholder?: string;

  /**
   * Флаг, указывающий, отключено ли поле.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Флаг, указывающий, является ли поле обязательным.
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Флаг, указывающий, можно ли очистить поле.
   *
   * @defaultValue `false`
   */
  clearable?: boolean;

  /**
   * Флаг, указывающий, находится ли поле в состоянии загрузки.
   *
   * @defaultValue `false`
   */
  loading?: boolean;

  /**
   * Тултип для поля формы.
   */
  tooltip?: ReactNode | null;

  /**
   * Вспомогательный текст под полем.
   */
  helperText?: string;

  /**
   * Текст, отображаемый, когда нет доступных опций.
   */
  noOptionsText?: string;

  /**
   * Функция для кастомного рендеринга опции.
   */
  renderOption?: (option: string) => ReactNode;

  /**
   * Максимальное количество отображаемых элементов.
   *
   * @defaultValue `50`
   */
  limit?: number;

  /**
   * Минимальная длина введённого значения для отображения подсказок.
   *
   * @defaultValue `1`
   */
  minInputValueLength?: number;

  /**
   * Определяет полноту заполненности адреса.
   *
   * @defaultValue `8`
   *
   * @remarks
   *
   * 0 — страна
   * 1 — регион
   * 3 — район
   * 4 — город
   * 5 — район города
   * 6 — населенный пункт
   * 7 — улица
   * 8 — дом
   * 9 — квартира или комната
   * 65 — планировочная структура
   * 75 — земельный участок
   * -1 — иностранный или пустой
   */
  addressCompleteness?: number;

  /**
   * Маска для поля ввода.
   */
  mask?: string;

  /**
   * Флаг, указывающий, нужно ли показывать ошибку без касания поля.
   *
   * @defaultValue `false`
   */
  validationWithoutTouch?: boolean;

  /**
   * Флаг, указывающий, нужно ли валидировать поле, когда оно отключено.
   *
   * @defaultValue `false`
   */
  validationWhenDisabled?: boolean;

  /**
   * Флаг, отключающий валидацию завершенности адреса (для смартвея и тривио)
   *
   * @defaultValue `undefined`
   */
  noAddressCompletenessValidation?: boolean;

  /**
   * Название каталога для SMARTWAY или TRIVIO
   *
   * @defaultValue `undefined`
   */
  catalogName?: string;
}

/**
 * Компонент ввода с подсказками.
 *
 * @public
 * @param name - Имя поля формы.
 * @param loadItems - Функция для загрузки элементов.
 * @param label - Текст метки для поля формы.
 * @param labelIcon - Иконка для метки.
 * @param onChange - Дополнительная функция-обработчик изменения значения.
 * @param placeholder - Плейсхолдер для поля ввода.
 * @param disabled - Флаг, указывающий, отключено ли поле.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param clearable - Флаг, указывающий, можно ли очистить поле.
 * @param loading - Флаг, указывающий, находится ли поле в состоянии загрузки.
 * @param tooltip - Тултип для поля формы.
 * @param helperText - Вспомогательный текст под полем.
 * @param noOptionsText - Текст, отображаемый, когда нет доступных опций.
 * @param renderOption - Функция для кастомного рендеринга опции.
 * @param limit - Максимальное количество отображаемых элементов.
 * @param minInputValueLength - Минимальная длина введённого значения для отображения подсказок.
 * @param addressCompleteness - Определяет полноту заполненности адреса.
 * @param mask - Маска для поля ввода.
 * @param validate - Правила валидации.
 * @param validationWithoutTouch - Флаг, указывающий, нужно ли показывать ошибку без касания поля.
 * @param validationWhenDisabled - Флаг, указывающий, нужно ли валидировать поле, когда оно отключено.
 * @param rest - Остальные пропсы, передаваемые в {@link useField}.
 * @returns Компонент Field, который рендерит кастомный компонент ввода Input.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link AddressInputProps}
 */
export const AddressInput = (
  props: AddressInputProps &
    FieldProps<string | undefined, FieldRenderProps<string | undefined>>,
) => {
  const {
    name,
    label,
    labelIcon,
    onChange,
    placeholder,
    disabled = false,
    required = false,
    loading = false,
    tooltip,
    helperText,
    noOptionsText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    addressCompleteness = 8,
    mask,
    validate,
    validationWithoutTouch = false,
    validationWhenDisabled = false,
    noAddressCompletenessValidation,
    catalogName,
    loadItems,
    ...rest
  } = props;

  const cacheItems = useRef(
    new Map<string, { value: string; addressCompleteness?: number }[]>(),
  ).current;

  const requestCache = useRef(
    new Map<
      string,
      Promise<{ value: string; addressCompleteness?: number }[]>
    >(),
  ).current;

  const dedupedLoadItems = (filter: Filter) => {
    const key = `${filter.query}|${filter.offset}|${filter.limit}`;
    const cached = requestCache.get(key);

    if (cached) {
      return cached;
    }

    const request = loadItems(filter).catch((error) => {
      requestCache.delete(key);
      throw error;
    });

    lruSet(requestCache, key, request);

    return request;
  };

  const internalValidErr = async (value?: string) => {
    if (!value) {
      return undefined;
    }

    if (value && value?.length < minInputValueLength) {
      return 'Минимальное количество символов > 1';
    }

    try {
      let list: { value: string; addressCompleteness?: number }[] | undefined =
        [];

      if (value && cacheItems.has(value)) {
        list = cacheItems.get(value);
      } else {
        list = await dedupedLoadItems({
          limit,
          offset: 0,
          query: (value || '').replace(/_+/g, '').trim(),
        });

        if (!value) {
          return undefined;
        }

        lruSet(cacheItems, value, list);
      }

      const item = list?.find((el) => el.value === value);
      const completeness = item?.addressCompleteness || 0;

      if (
        !noAddressCompletenessValidation &&
        addressCompleteness > completeness
      ) {
        return completenessErr;
      }
    } catch {
      return noAddressCompletenessValidation
        ? `Поиск городов в ${catalogName === 'SMARTWAY' ? 'Smartway' : 'Trivio'} временно недоступен. Обратитесь, пожалуйста, в техподдержку вашей компании`
        : requestErr;
    }

    return undefined;
  };

  const { input, meta } = useField(name, {
    validate: composeValidators(
      validate ? [validate, internalValidErr] : [internalValidErr],
    ),
    ...rest,
  });

  const touched = meta.touched || meta.error === requestErr;

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled.
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки.
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : touched && !!meta.error;

  return (
    <Input
      {...input}
      label={label}
      labelIcon={labelIcon}
      onChange={handleChange(input, onChange)}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      loading={loading}
      tooltip={tooltip}
      error={renderError}
      helperText={(renderError && meta.error) || helperText}
      noOptionsText={noOptionsText}
      renderOption={renderOption}
      loadItems={dedupedLoadItems}
      limit={limit}
      mask={mask}
      addressCompleteness={addressCompleteness}
      minInputValueLength={minInputValueLength}
    />
  );
};
